import moment from 'moment'
import { getRESTParams, postRESTParams, REMBLE_API_URL } from '.'

const BASE_PATH = `${REMBLE_API_URL}/payments`

const KEY_SIGNUP_CODE = 'signup_code'

const TRIAL_DURATION = 7 // days

const DISCOUNT_PERIOD_ONCE = 'ONCE'
const DISCOUNT_PERIOD_INDEFINITE = 'INDEFINITE'

const DISCOUNT_TYPE_PERCENT = 'PERCENT'
const DISCOUNT_TYPE_DOLLAR = 'DOLLAR'
const DISCOUNT_TYPE_FIXED_PRICE = 'FIXED_PRICE'

const PAYMENT_TERM_ANNUAL = 'annual'
const PAYMENT_TERM_MONTHLY = 'monthly'
const PAYMENT_TERM_QUARTERLY = 'quarterly'

const SUBSCRIPTION_PERIOD_ANNUAL = 'PERIOD_1Y'
const SUBSCRIPTION_PERIOD_MONTHLY = 'PERIOD_1M'
const SUBSCRIPTION_PERIOD_QUARTERLY = 'PERIOD_3M'

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Common Payments GET request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See getRESTParams method for more info.
 */
const _paymentsRESTGET = async (url, params) => {
  const result = await fetch(url, getRESTParams(params))
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
      return { error }
    })

  return result
}

/**
 * Common Payments POST request pattern.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _paymentsRESTPOST = async (url, params) =>
  _paymentsRESTGET(
    url,
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Returns a JSON object with the user's subscription details
 */
const getCurrentSubscription = async () => {
  const url = `${BASE_PATH}/subscription`
  const result = await _paymentsRESTGET(url)
  return result
}

/**
 * Returns an array with a list of all active plan options
 */
const getSubscriptionPlans = async () => {
  const url = `${BASE_PATH}/plans`
  const result = await _paymentsRESTGET(url)
  return result?.plans ? result.plans : []
}

/**
 * Returns a "human readable" version of the subscription renewal period or an empty string
 * @param {Object} subscription The user's current subscription
 */
const getReadableSubscriptionPeriod = subscription => {
  const periods = {}
  periods[SUBSCRIPTION_PERIOD_ANNUAL] = 'Yearly'
  periods[SUBSCRIPTION_PERIOD_MONTHLY] = 'Monthly'
  periods[SUBSCRIPTION_PERIOD_QUARTERLY] = 'Quarterly'

  return periods[subscription.plan_period] || ''
}

/**
 * Returns the number of months in a given renewal period
 * @param {String} period The period to get the number of months for
 */
const getMonthsInPeriod = period => {
  const months = {}
  months[SUBSCRIPTION_PERIOD_ANNUAL] = 12
  months[SUBSCRIPTION_PERIOD_MONTHLY] = 1
  months[SUBSCRIPTION_PERIOD_QUARTERLY] = 3

  return months[period] || 0
}

/**
 * Returns the "average" number of days in a given renewal period
 * @param {String} period The period to get the number of days for
 */
const getDaysInPeriod = period => {
  const days = {}
  days[SUBSCRIPTION_PERIOD_ANNUAL] = 365
  days[SUBSCRIPTION_PERIOD_MONTHLY] = 30
  days[SUBSCRIPTION_PERIOD_QUARTERLY] = 90

  return days[period] || 0
}

/**
 * Gets the "monthly cost" for a given plan, by taking the total cost and dividing it by the
 * number of months in the plan's period
 * @param {Object} plan The plan to get the monthly cost for
 */
const getMonthlyPlanCost = plan => {
  let cost = 0
  const periodMonths = getMonthsInPeriod(plan.period)

  // Make sure we received a valid month value
  if (periodMonths) cost = (plan.cost / periodMonths).toFixed(2)

  return cost
}

/**
 * Gets the annualized cost for a given subscription
 * @param {Object} subscription The subscription to get the annual cost for
 */
const getAnnualSubscriptionCost = subscription => {
  let cost = 0
  const periodMonths = getMonthsInPeriod(subscription.plan_period)

  // Make sure we received a valid month value
  if (periodMonths) cost = (12 / periodMonths) * subscription.subscription_cost

  return cost
}

/**
 * Returns the estimated number of days remaining in a given subscription and returns the ceiling value
 * @param {Object} subscription The subscription to get the remaining number of days for
 * @param {String} dateField The date field to use for the date calculation. Could be 'renews_on' or 'ends_on'
 */
const getDaysRemaining = (subscription, dateField = 'ends_on') =>
  Math.max(
    0,
    Math.ceil(moment(subscription[dateField]).diff(moment(), 'days', true))
  )

/**
 * Cancels the user's subscription
 * @param {Object} payload The payload data to send with the cancellation
 * @returns Returns an object with an error or an object with a `success` boolean property
 */
const cancelSubscription = async ({ reason, feedback }) => {
  const url = `${BASE_PATH}/cancel_subscription`

  const payload = {
    reason,
    feedback
  }

  const body = JSON.stringify(payload)

  const result = await _paymentsRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  // Did we have an error
  return result.detail ? { error: result.detail } : result
}

/**
 * Posts user payment data to the /users/payments/subscribe endpoint to initialize a new subscriber
 * @param {Object} payload The payload data to initiate the workflow. Must include a `plan` object with
 * subscription details and a `card` object with credit card details
 * @returns Returns an object with an error or an object with the post response data
 */
const subscribe = async ({
  plan,
  card,
  signup_code,
  customData = [],
  in_app_purchase = false
}) => {
  const url = `${BASE_PATH}/subscribe`

  // NOTE: Free user accounts don't require a card, so it could be empty
  const payload = {
    signup_code,
    in_app_purchase,
    first_name: card?.firstName,
    last_name: card?.lastName,
    card_number: card?.cardNumber,
    card_verification: card?.cardVerification,
    zip: card?.zip,
    card_expiration: card?.cardExpiration
      ? card.cardExpiration.replace('/', '')
      : '', // Drop slashes
    recurring_schedule: plan.period,
    plan_id: plan.id,
    custom_data: customData
  }

  const body = JSON.stringify(payload)

  const saved = await _paymentsRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  // Did we have an error
  return saved.detail ? { error: saved.detail } : saved
}

/**
 * Posts user trial data to the /users/payments/trial endpoint to initialize a new trial
 * @param {Object} payload The payload data for the trial
 * @returns Returns an object with an error or an object with the post response data
 */
const startTrial = async ({ customData = [] }) => {
  const url = `${BASE_PATH}/trial`
  const payload = {
    custom_data: customData
  }

  const body = JSON.stringify(payload)

  const saved = await _paymentsRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  // Did we have an error
  return saved.detail ? { error: saved.detail } : saved
}

/**
 * Posts signup code to the /users/payments/verify_code endpoint to verify the signup code
 * @param {String} code The signup code the user received
 * @returns Returns an object with an error or an object indicating whether the code is valid and
 * if so, what the discount is
 */
const verifySignupCode = async code => {
  const url = `${BASE_PATH}/verify_code`
  const body = JSON.stringify({
    code
  })

  const result = await _paymentsRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  // Did we have an error
  return result.detail ? { error: result.detail } : result
}

/**
 * Returns promottional pricing information for all plans
 * @returns Returns an array containing all plans with promotional pricing information
 */
const getPlanPromos = () => [
  {
    recommendation: 'Save 50%',
    period_readable: 'Yearly',
    period: SUBSCRIPTION_PERIOD_ANNUAL,
    sku: 'RemblePremiumYearly',
    full_monthly_price: '$179.88',
    discounted_monthly_price: '$7.49',
    summary: '12 months'
  },
  {
    recommendation: '',
    period_readable: 'Quarterly',
    period: SUBSCRIPTION_PERIOD_QUARTERLY,
    sku: 'RemblePremiumQuarterly',
    full_monthly_price: '$59.97',
    discounted_monthly_price: '$13.34'
  },
  {
    recommendation: '',
    period_readable: 'Monthly',
    period: SUBSCRIPTION_PERIOD_MONTHLY,
    sku: 'RemblePremiumMonthly',
    full_monthly_price: ''
  }
]

export {
  getAnnualSubscriptionCost,
  getDaysRemaining,
  getDaysInPeriod,
  getMonthlyPlanCost,
  getMonthsInPeriod,
  getReadableSubscriptionPeriod,
  getPlanPromos,
  getCurrentSubscription,
  getSubscriptionPlans,
  subscribe,
  startTrial,
  cancelSubscription,
  verifySignupCode,
  DISCOUNT_PERIOD_INDEFINITE,
  DISCOUNT_PERIOD_ONCE,
  DISCOUNT_TYPE_DOLLAR,
  DISCOUNT_TYPE_PERCENT,
  DISCOUNT_TYPE_FIXED_PRICE,
  KEY_SIGNUP_CODE,
  PAYMENT_TERM_ANNUAL,
  PAYMENT_TERM_MONTHLY,
  PAYMENT_TERM_QUARTERLY,
  SUBSCRIPTION_PERIOD_ANNUAL,
  SUBSCRIPTION_PERIOD_MONTHLY,
  SUBSCRIPTION_PERIOD_QUARTERLY,
  TRIAL_DURATION
}
